import React from 'react';
import {
  Box,
  Flex,
  Button,
  useToast,
  IconButton,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate, Outlet, useLocation } from 'react-router-dom';
import DialerrWidget from '../components/admin/DialerrWidget';
import { FaHome, FaUsers, FaVials, FaFileMedical, FaUserMd, FaFlask, FaUserInjured, FaUpload, FaDollarSign, FaFileInvoiceDollar, FaClipboardList, FaFileContract, FaCog, FaFax, FaHospital, FaRoute, FaSync, FaMapMarkedAlt, FaClone, FaTasks, FaBoxOpen, FaClock, FaVial, FaMapMarkerAlt } from 'react-icons/fa';

function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  const navBtn = (to, icon, label) => (
    <Button
      as={RouterLink}
      to={to}
      leftIcon={icon}
      variant="ghost"
      color="white"
      _hover={{ color: 'blue.200', bg: 'gray.700' }}
      size="sm"
      px={2}
      fontSize="13px"
      bg={isActive(to) ? 'gray.700' : undefined}
      flexShrink={0}
      whiteSpace="nowrap"
      iconSpacing={1}
    >
      {label}
    </Button>
  );

  const handleLogout = () => {
    localStorage.removeItem('adminUser');
    // Clear the Dialerr agent session cookie so the softphone doesn't stay signed in.
    try { fetch('/api/dialerr/session', { method: 'DELETE', credentials: 'same-origin' }); } catch (e) { /* ignore */ }
    navigate('/admin/login');
    toast({
      title: 'Logged out successfully',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <Box minH="100vh" bg="gray.50">
      {adminUser?.id && (
        <Box as="nav" bg="gray.800" color="white" position="sticky" top={0} zIndex={1000}>
          <Flex align="center" px={4} py={2}>
            <Flex align="center" flex={1} gap={1}>
              {navBtn('/admin', <FaHome />, 'Dashboard')}
              {navBtn('/admin/today', <FaTasks />, 'Today')}
              {navBtn('/admin/inventory', <FaBoxOpen />, 'Inventory')}
              {navBtn('/admin/phlebotomists', <FaVials />, 'Phlebotomists')}
              {navBtn('/admin/patients', <FaUserInjured />, 'Patients')}
              {navBtn('/admin/labs', <FaFlask />, 'Labs')}
              {navBtn('/admin/billing', <FaDollarSign />, 'Billing')}
              {/* Invoices now live as a tab under Billing */}
              {/* Directories now live as a tab under Labs */}
              {navBtn('/admin/agreements', <FaFileContract />, 'Agreements')}
              {navBtn('/admin/test-types', <FaUpload />, 'Tests')}
              {navBtn('/admin/submissions', <FaFileMedical />, 'Blood Files')}
          {/* Duplicates moved under the Blood Files page */}
              {navBtn('/admin/test-offerings', <FaCog />, 'Offerings')}
              {navBtn('/admin/doctors', <FaUserMd />, 'Doctors')}
              {navBtn('/admin/facilities', <FaHospital />, 'Facilities')}
              {/* Route Planner hidden for now */}
          {navBtn('/admin/active-routes', <FaMapMarkedAlt />, 'Active Routes')}
              {navBtn('/admin/phleb-hours', <FaClock />, 'Phleb Hours')}
              {navBtn('/admin/ready-for-drop', <FaVial />, 'Ready for Drop')}
              {navBtn('/admin/drop-spots', <FaMapMarkerAlt />, 'Drop Spots')}
              {navBtn('/admin/fax', <FaFax />, 'Fax')}
              {/* Lab API moved under the Blood Files page */}
            </Flex>
            {adminUser?.role === 'master' && (
              <IconButton
                as={RouterLink}
                to="/admin/users"
                icon={<FaUsers />}
                aria-label="Admin Management"
                variant="ghost"
                color="white"
                size="sm"
                _hover={{ color: 'blue.200', bg: 'gray.700' }}
                title="Admin Management"
                flexShrink={0}
              />
            )}
            <Button colorScheme="red" variant="solid" size="sm" onClick={handleLogout} ml={1} flexShrink={0}>
              Logout
            </Button>
          </Flex>
        </Box>
      )}
      {/* Main Content */}
      <Box maxW="100vw" p={8}>
        <Outlet context={{ adminUser }} />
      </Box>
      {/* Softphone — admin panel only, and only once signed in. */}
      {adminUser?.id && <DialerrWidget />}
    </Box>
  );
}

export default AdminLayout;
